"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useGetSchema = void 0;
var _culture = require("@ori/culture");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _dateFns = require("date-fns");
var _zod = require("zod");
var _hooks = require("../hooks");
var _models = require("../models");
var _utils = require("../utils");
const createFieldSchema = (formatMessage, fieldConfig, type, culturePattern, exceptionField) => {
  const correctCulturePattern = culturePattern ?? '';
  const dateFormat = (0, _utils.generateDateFormat)(correctCulturePattern);
  let correctRegexMessageKey = _models.TranslationKey.validationRegex;
  switch (exceptionField) {
    case 'uniqueId':
      correctRegexMessageKey = _models.TranslationKey.validationRegexUniqueID;
      break;
    case 'email':
      correctRegexMessageKey = _models.TranslationKey.validationRegexEmail;
      break;
    case 'phone':
      correctRegexMessageKey = _models.TranslationKey.validationRegexPhone;
      break;
    default:
      correctRegexMessageKey = _models.TranslationKey.validationRegex;
  }
  if (!fieldConfig?.validation?.isMandatory) {
    switch (type) {
      case 'string':
        return _zod.z.string().nullish().refine(val => !val || new RegExp(fieldConfig?.validation?.regularExpression ?? '').test(val), formatMessage(correctRegexMessageKey));
      case 'number':
        return _zod.z.number().nullish();
      case 'date':
        return _zod.z.date({
          errorMap: (issue, {
            defaultError
          }) => ({
            message: issue.code === 'invalid_date' ? formatMessage(_models.TranslationKey.validationBirthDateRegex, {
              correctFormat: dateFormat.toUpperCase()
            }) : defaultError
          })
        }).nullish();
      case 'boolean':
        return _zod.z.boolean().nullish();
      case 'genderEnum':
        return _zod.z.nativeEnum(_globalApiClientReact.OptionalNullableCustomerGenderValue).nullish();
      default:
        throw new Error(`Unsupported type: ${type}`);
    }
  }
  let fieldSchema;
  const minDate = fieldConfig.validation.minValue ? new Date(fieldConfig.validation.minValue) : undefined;
  const maxDate = fieldConfig.validation.maxValue ? new Date(fieldConfig.validation.maxValue) : undefined;
  const formattedMax = maxDate ? (0, _dateFns.format)(maxDate, correctCulturePattern).replaceAll(' ', '') : null;
  const formattedMin = minDate ? (0, _dateFns.format)(minDate, correctCulturePattern).replaceAll(' ', '') : null;
  switch (type) {
    case 'string':
      fieldSchema = _zod.z.string({
        message: formatMessage(_models.TranslationKey.validationRequired)
      }).min(1, {
        message: formatMessage(_models.TranslationKey.validationRequired)
      }).regex(new RegExp(fieldConfig.validation.regularExpression ?? ''), formatMessage(correctRegexMessageKey)).min(fieldConfig.validation.minLength ?? 1, formatMessage(_models.TranslationKey.validationMinLength, {
        min: fieldConfig.validation.minLength
      })).max(fieldConfig.validation.maxLength ?? 255, formatMessage(_models.TranslationKey.validationMaxLength, {
        max: fieldConfig.validation.maxLength
      }));
      break;
    case 'number':
      fieldSchema = _zod.z.number({
        message: formatMessage(_models.TranslationKey.validationRequired)
      });
      break;
    case 'date':
      fieldSchema = _zod.z.date({
        message: formatMessage(_models.TranslationKey.validationRequired),
        errorMap: (issue, {
          defaultError
        }) => ({
          message: issue.code === 'invalid_date' ? formatMessage(_models.TranslationKey.validationBirthDateRegex, {
            correctFormat: dateFormat.toUpperCase()
          }) : defaultError
        })
      }).min(fieldConfig.validation.minValue ?? new Date(), formatMessage(_models.TranslationKey.validationMinValue, {
        min: formattedMin
      })).max(fieldConfig.validation.maxValue ?? new Date(), formatMessage(_models.TranslationKey.validationMaxValue, {
        max: formattedMax
      }));
      break;
    case 'boolean':
      fieldSchema = _zod.z.boolean().refine(val => val, {
        message: formatMessage(_models.TranslationKey.validationRequired)
      });
      break;
    case 'genderEnum':
      fieldSchema = _zod.z.union([_zod.z.nativeEnum(_globalApiClientReact.OptionalNullableCustomerGenderValue), _zod.z.null(), _zod.z.undefined()]).refine(val => val !== undefined && val !== null, {
        message: formatMessage(_models.TranslationKey.validationRequired)
      });
      break;
    default:
      throw new Error(`Unsupported type: ${type}`);
  }
  return fieldSchema;
};
const useGetSchema = config => {
  const {
    formatMessage
  } = (0, _hooks.useTranslations)();
  const {
    culture
  } = (0, _culture.useCulture)();
  const culturePattern = culture?.calendarFormat?.patterns?.shortDatePattern;
  if (!config) {
    return _zod.z.object({});
  }
  const {
    contactDetails,
    identityDetails,
    address,
    legalDetails,
    parentalConsent,
    personalDetails,
    sponsorDetails
  } = config;
  let schema = _zod.z.object({});
  schema = _zod.z.object({
    personalDetails: _zod.z.object({
      firstName: _zod.z.object({
        value: createFieldSchema(formatMessage, personalDetails?.firstName, 'string')
      }).nullish(),
      lastName: _zod.z.object({
        value: createFieldSchema(formatMessage, personalDetails?.lastName, 'string')
      }),
      uniqueId: _zod.z.object({
        value: createFieldSchema(formatMessage, personalDetails?.uniqueId, 'string', undefined, 'uniqueId')
      }).nullish(),
      taxCode: _zod.z.object({
        value: createFieldSchema(formatMessage, personalDetails?.taxCode, 'string')
      }).nullish(),
      taxCode2: _zod.z.object({
        value: createFieldSchema(formatMessage, personalDetails?.taxCode2, 'string')
      }).nullish(),
      passport: _zod.z.object({
        value: createFieldSchema(formatMessage, personalDetails?.passport, 'string')
      }).nullish(),
      birthday: _zod.z.object({
        value: createFieldSchema(formatMessage, personalDetails?.birthDate, 'date', culturePattern, 'birthDate')
      }).nullish(),
      gender: _zod.z.object({
        value: createFieldSchema(formatMessage, personalDetails?.gender, 'genderEnum')
      }).nullish()
    }),
    identityDetails: _zod.z.object({
      password: _zod.z.object({
        value: createFieldSchema(formatMessage, identityDetails?.password, 'string')
      }),
      userAccountCreated: _zod.z.boolean().optional()
    }),
    contactDetails: _zod.z.object({
      contactTelephone: _zod.z.object({
        value: createFieldSchema(formatMessage, contactDetails?.telephone, 'string', undefined, 'phone')
      }),
      contactEmail: _zod.z.object({
        value: createFieldSchema(formatMessage, contactDetails?.email, 'string', undefined, 'email')
      })
    }).nullish(),
    sponsorDetails: _zod.z.object({
      internationalPbsCode: _zod.z.object({
        value: createFieldSchema(formatMessage, sponsorDetails?.internationalPbsCode, 'string')
      }).optional(),
      sponsorNumber: _zod.z.object({
        value: createFieldSchema(formatMessage, sponsorDetails?.sponsorNumber, 'number')
      }).optional(),
      invitationCode: _zod.z.object({
        value: createFieldSchema(formatMessage, sponsorDetails?.invitationCode, 'string')
      }).optional()
    }).nullish(),
    parentalConsent: _zod.z.object({
      telephone: _zod.z.object({
        value: createFieldSchema(formatMessage, parentalConsent?.telephone, 'string', undefined, 'phone')
      }).nullish(),
      email: _zod.z.object({
        value: createFieldSchema(formatMessage, parentalConsent?.email, 'string', undefined, 'email')
      }).nullish()
    }).nullish(),
    legalDetails: _zod.z.object({
      isReseller: _zod.z.object({
        value: _zod.z.boolean().optional()
      }).nullish(),
      confirmation1: _zod.z.object({
        value: createFieldSchema(formatMessage, legalDetails?.confirmation1, 'boolean')
      }).nullish(),
      confirmation2: _zod.z.object({
        value: createFieldSchema(formatMessage, legalDetails?.confirmation2, 'boolean')
      }).nullish(),
      approvals: _zod.z.object({
        personalData: _zod.z.boolean().optional(),
        contact: _zod.z.boolean().optional(),
        marketingOriflame: _zod.z.boolean().optional(),
        marketingExternal: _zod.z.boolean().optional(),
        publication: _zod.z.boolean().optional(),
        newsletters: _zod.z.boolean().optional(),
        uplineLeaderData: _zod.z.boolean().optional(),
        uplineLeaderBrowse: _zod.z.boolean().optional(),
        orderHistory: _zod.z.boolean().optional(),
        marketingConsultant: _zod.z.boolean().optional(),
        blockGuaranteeOrdering: _zod.z.boolean().optional(),
        optedOutOfSponsorSelect: _zod.z.boolean().optional()
      }).nullish(),
      termsAndConditions: _zod.z.object({
        termId: createFieldSchema(formatMessage, legalDetails?.termsAndConditions, 'string')
      }).nullish()
    }),
    address: _zod.z.object({
      country: createFieldSchema(formatMessage, address?.country, 'string'),
      region: createFieldSchema(formatMessage, address?.region, 'string'),
      province: createFieldSchema(formatMessage, address?.province, 'string'),
      district: createFieldSchema(formatMessage, address?.district, 'string'),
      area: createFieldSchema(formatMessage, address?.area, 'string'),
      city: createFieldSchema(formatMessage, address?.city, 'string'),
      locality: createFieldSchema(formatMessage, address?.locality, 'string'),
      streetAddress: createFieldSchema(formatMessage, address?.streetAddress, 'string'),
      zipcode: createFieldSchema(formatMessage, address?.zipcode, 'string'),
      delivery: createFieldSchema(formatMessage, address?.delivery, 'string'),
      delivery2: createFieldSchema(formatMessage, address?.delivery2, 'string'),
      delivery3: createFieldSchema(formatMessage, address?.delivery3, 'string')
    }).nullish()
  }).superRefine((data, ctx) => {
    const contactTelephone = data.contactDetails?.contactTelephone.value;
    const contactEmail = data.contactDetails?.contactEmail.value;
    const parentalTelephone = data.parentalConsent?.telephone?.value;
    const parentalEmail = data.parentalConsent?.email?.value;
    if (contactTelephone && parentalTelephone && contactTelephone === parentalTelephone) {
      ctx.addIssue({
        path: ['parentalConsent.telephone.value'],
        message: formatMessage(_models.TranslationKey.ParentalConsentSamePhoneValidation),
        code: _zod.z.ZodIssueCode.custom
      });
    }
    if (contactEmail && parentalEmail && contactEmail === parentalEmail) {
      ctx.addIssue({
        path: ['parentalConsent.email.value'],
        message: formatMessage(_models.TranslationKey.ParentalConsentSameEmailValidation),
        code: _zod.z.ZodIssueCode.custom
      });
    }
  });
  return schema;
};
exports.useGetSchema = useGetSchema;
